import type { GridApi, GridColDef, GridRowId } from '@repo/go-table';
import type { MutableRefObject } from 'react';

import type { TFactTrajectoryPoint } from '../../service/model';
import type { TrajectoryService } from '../../service/trajectory-service';

import { SERVICE_COLUMNS } from './display-manager';
import { isTrajectoryEditableField, type TTrajectoryEditableField } from './editable-fields';

type TypeTrajStationOption = { value: string; label: string };

type PasteCell = {
  rowId: GridRowId;
  field: string;
};

type PasteTarget = {
  startRowId: GridRowId;
  startColumnIndex: number;
  selectedCells: PasteCell[];
};

const NUMERIC_PASTE_FIELDS = new Set<TTrajectoryEditableField>(['md', 'inc', 'azi', 'mtf', 'gtf']);

const sortVisibleRowIds = (api: GridApi, rowIds: readonly GridRowId[]): GridRowId[] =>
  rowIds
    .map((rowId) => ({ rowId, index: api.getRowIndexRelativeToVisibleRows(rowId) }))
    .filter(({ index }) => index >= 0)
    .sort((left, right) => left.index - right.index)
    .map(({ rowId }) => rowId);

const getPasteColumns = (api: GridApi): GridColDef[] =>
  api.getVisibleColumns().filter(({ field }) => isTrajectoryEditableField(field) && !SERVICE_COLUMNS.includes(field));

const getPasteTarget = (api: GridApi, columns: readonly GridColDef[]): PasteTarget | null => {
  const selection = api.getCellSelectionModel();
  const columnIndexes = new Map(columns.map(({ field }, index) => [field, index]));
  const selectedCells = sortVisibleRowIds(api, Object.keys(selection)).flatMap((rowId) =>
    columns.flatMap(({ field }) => (selection[rowId]?.[field] ? [{ rowId, field }] : [])),
  );

  if (selectedCells.length > 0) {
    const startRowId = selectedCells[0].rowId;
    const startColumnIndex = Math.min(...selectedCells.map(({ field }) => columnIndexes.get(field) ?? Infinity));

    return { startRowId, startColumnIndex, selectedCells };
  }

  const focusedCell = api.state.focus.cell;
  const startColumnIndex = focusedCell ? columnIndexes.get(focusedCell.field) : undefined;

  if (!focusedCell || startColumnIndex === undefined) {
    return null;
  }

  return {
    startRowId: focusedCell.id,
    startColumnIndex,
    selectedCells: [{ rowId: focusedCell.id, field: focusedCell.field }],
  };
};

const normalizeOptionText = (value: string): string => value.trim().toLocaleLowerCase();

const parseTypeTrajStationValue = (
  rawValue: string,
  typeTrajStationOptions: readonly TypeTrajStationOption[],
): string | null | undefined => {
  const trimmed = rawValue.trim();

  if (!trimmed) {
    return null;
  }

  const normalizedValue = normalizeOptionText(trimmed);
  const matchedOptions = typeTrajStationOptions.filter(
    ({ value, label }) =>
      normalizeOptionText(value) === normalizedValue || normalizeOptionText(label) === normalizedValue,
  );

  if (matchedOptions.length !== 1) {
    return undefined;
  }

  return matchedOptions[0].value;
};

const parseFieldValue = (
  field: TTrajectoryEditableField,
  rawValue: string,
  typeTrajStationOptions: readonly TypeTrajStationOption[],
): Partial<TFactTrajectoryPoint> | null | undefined => {
  if (field === 'comment') {
    return { comment: rawValue || null };
  }

  if (field === 'typeTrajStation') {
    const parsedValue = parseTypeTrajStationValue(rawValue, typeTrajStationOptions);

    if (parsedValue === undefined) {
      return undefined;
    }

    return { typeTrajStation: parsedValue };
  }

  if (!NUMERIC_PASTE_FIELDS.has(field)) {
    return null;
  }

  const trimmed = rawValue.trim();

  if (!trimmed) {
    return { [field]: null };
  }

  const normalizedNumber = Number(trimmed.replace(',', '.'));

  if (Number.isNaN(normalizedNumber)) {
    return undefined;
  }

  return { [field]: normalizedNumber };
};

const buildPatchFromClipboardRow = (
  clipboardRow: string[],
  pasteColumns: readonly GridColDef[],
  typeTrajStationOptions: readonly TypeTrajStationOption[],
): Partial<TFactTrajectoryPoint> => {
  const patch: Partial<TFactTrajectoryPoint> = {};

  clipboardRow.forEach((rawValue, columnIndex) => {
    const field = pasteColumns[columnIndex]?.field;

    if (!field || !isTrajectoryEditableField(field)) {
      return;
    }

    const parsedValue = parseFieldValue(field, rawValue, typeTrajStationOptions);

    if (!parsedValue) {
      return;
    }

    Object.assign(patch, parsedValue);
  });

  return patch;
};

const applyClipboardRowToEntity = (
  trajectoryService: TrajectoryService,
  entityId: string,
  clipboardRow: string[],
  pasteColumns: readonly GridColDef[],
  typeTrajStationOptions: readonly TypeTrajStationOption[],
): void => {
  const point = trajectoryService.mainFactTrajectory?.pointsMap.get(entityId);

  if (!point || trajectoryService.isMainPointLocked(point)) {
    return;
  }

  const patch = buildPatchFromClipboardRow(clipboardRow, pasteColumns, typeTrajStationOptions);

  if (Object.keys(patch).length === 0) {
    return;
  }

  trajectoryService.updateMainPointField(entityId, patch);
};

type HandleTrajectoryPasteOptions = {
  tableApi: MutableRefObject<GridApi>;
  trajectoryService: TrajectoryService;
  clipboardRows: string[][];
  isEditing: boolean;
  typeTrajStationOptions: readonly TypeTrajStationOption[];
};

export function handleTrajectoryPaste({
  tableApi,
  trajectoryService,
  clipboardRows,
  isEditing,
  typeTrajStationOptions,
}: HandleTrajectoryPasteOptions): void {
  if (!isEditing || trajectoryService.areMainStationsReadOnly) {
    return;
  }

  const api = tableApi.current;
  const columns = getPasteColumns(api);
  const target = getPasteTarget(api, columns);
  const filteredRows = clipboardRows.filter((row) => !row.every((value) => !value?.trim()));

  if (!target || filteredRows.length === 0) {
    return;
  }

  const pasteColumns = columns.slice(target.startColumnIndex);
  const isSingleValue = filteredRows.length === 1 && filteredRows[0].length === 1;

  if (isSingleValue && target.selectedCells.length > 1) {
    const rawValue = filteredRows[0][0];

    for (const { rowId, field } of target.selectedCells) {
      if (!isTrajectoryEditableField(field)) {
        continue;
      }

      const parsedValue = parseFieldValue(field, rawValue, typeTrajStationOptions);

      if (!parsedValue) {
        continue;
      }

      trajectoryService.updateMainPointField(String(rowId), parsedValue);
    }

    return;
  }

  const visibleRowIds = sortVisibleRowIds(
    api,
    api.getSortedRowIds().filter((rowId) => api.getRowIndexRelativeToVisibleRows(rowId) >= 0),
  );
  const startRowIndex = visibleRowIds.findIndex((rowId) => String(rowId) === String(target.startRowId));

  if (startRowIndex < 0) {
    return;
  }

  const existingRowIds = visibleRowIds.slice(startRowIndex, startRowIndex + filteredRows.length);

  filteredRows.forEach((clipboardRow, rowIndex) => {
    let entityId: string | undefined;

    if (rowIndex < existingRowIds.length) {
      entityId = String(existingRowIds[rowIndex]);
    } else {
      const point = trajectoryService.addMainPoint();

      if (!point) {
        return;
      }

      entityId = point.entityId;
    }

    applyClipboardRowToEntity(trajectoryService, entityId, clipboardRow, pasteColumns, typeTrajStationOptions);
  });
}
